import { alpha } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import type { DensityConfig } from './listView.types';

interface StyleProps {
  density: DensityConfig;
}

export default makeStyles<Theme, StyleProps>((theme) => ({
  tableContainer: {
    width: '100%',
    overflowX: 'auto',
    backgroundColor: theme.palette.background.paper,
    '&::-webkit-scrollbar': {
      height: 6,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.background.default,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: alpha(theme.palette.text.primary, 0.2),
      borderRadius: 3,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.35),
    },
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: theme.palette.background.paper,
  },

  tableHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: theme.palette.grey[900],
  },

  headerRow: {},

  headerCell: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontSize: ({ density }) => density.headerFontSize,
    fontWeight: 500,
    letterSpacing: '0.08em',
    borderBottom: `1px solid ${theme.palette.grey[700]}`,
    position: 'relative',
    padding: ({ density }) => `${density.cellPaddingY}px ${density.cellPaddingX}px`,
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
  },

  resizeHandle: {
    position: 'absolute',
    right: 0,
    top: '25%',
    height: '50%',
    width: 4,
    cursor: 'col-resize',
    opacity: 0,
    transition: 'opacity 150ms ease',
    '$headerCell:hover &': {
      opacity: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 1,
      top: 0,
      bottom: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
    },
    '&:active': {
      opacity: 1,
      '&::after': {
        backgroundColor: theme.palette.secondary.main,
      },
    },
  },

  bodyRow: {
    transition: 'background-color 150ms ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      boxShadow: `inset 2px 0 0 ${theme.palette.secondary.main}`,
    },
  },

  bodyRowOdd: {
    backgroundColor: alpha(theme.palette.common.white, 0.03),
  },

  bodyRowSelected: {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    boxShadow: `inset 2px 0 0 ${theme.palette.primary.main}`,
  },

  bodyRowDragging: {
    opacity: 0.5,
  },

  bodyRowGallery: {
    backgroundColor: alpha(theme.palette.secondary.main, 0.03),
    borderLeft: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
  },

  bodyCell: {
    padding: ({ density }) => `${density.cellPaddingY}px ${density.cellPaddingX}px`,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: ({ density }) => density.fontSize,
    color: theme.palette.text.secondary,
  },

  selectCell: {
    textAlign: 'center',
    padding: 0,
  },

  typeCell: {
    textAlign: 'center',
  },

  thumbnailCell: {},

  thumbnailImage: {
    width: ({ density }) => density.thumbWidth,
    height: ({ density }) => density.thumbHeight,
    objectFit: 'cover',
    borderRadius: (theme.shape.borderRadius as number) / 2,
    backgroundColor: theme.palette.background.default,
  },

  thumbnailPlaceholder: {
    width: ({ density }) => density.thumbWidth,
    height: ({ density }) => density.thumbHeight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.disabled,
    backgroundColor: theme.palette.grey[600],
    borderRadius: (theme.shape.borderRadius as number) / 2,
  },

  nameCell: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    transition: 'color 150ms ease',
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },

  typeBadgeAsset: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.light,
  },

  typeBadgeGallery: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
    color: theme.palette.secondary.light,
  },

  emptyValue: {
    color: theme.palette.text.disabled,
    fontSize: '0.75rem',
  },

  actionsCell: {
    textAlign: 'center',
  },
}));
